import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ProjectsCards from './ProjectsCards'
import ProjectsListCN from './ProjectsListCN'
import Layout from 'components/Layout'
import Message from 'components/Message'
import Spinner from 'components/Spinner'
import useTable from 'components/controls/useTable'
import ProjectsEmptyState from 'components/ProjectsEmptyState'
import CreateProjectModal from './CreateProjectModal'
import useCapabilities from 'hooks/useCapabilities'
import { ViewToggle } from 'components/ViewToggle'
import { List, LayoutGrid, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react'
import * as _ from 'styles/helpers'
import { initializeMyProjects } from 'slices/projectSlice'

const headerCells = [
	{ id: 'Image', label: '', disableSorting: true },
	{ id: 'projectName', label: 'Project Name' },
	{ id: 'assetName', label: 'Asset Name' },
	{ id: 'owner.name', label: 'Originator', disableSorting: true },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'postalCode', label: 'Postal Code' },
]

const Projects = ({ cardDisplay, onToggleCardDisplay, isMobile, listOnly = isMobile }) => {
	const dispatch = useDispatch()
	const { canCreateAssets } = useCapabilities()

	const searchInputRef = useRef(null)
	const [searchInput, setSearchInput] = useState('')
	const [createOpen, setCreateOpen] = useState(false)

	// Card view sort state
	const [cardSortBy, setCardSortBy] = useState(null)
	const [cardSortOrder, setCardSortOrder] = useState('asc')

	const cardSortFields = [
		{ id: 'projectName', label: 'Name' },
		{ id: 'assetName', label: 'Location' },
		{ id: 'city', label: 'City' },
		{ id: 'updatedAt', label: 'Date Updated' },
	]

	const handleCardSort = fieldId => {
		if (cardSortBy === fieldId) {
			setCardSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setCardSortBy(fieldId)
			setCardSortOrder('asc')
		}
	}

	const [filterFn, setFilterFn] = useState({
		fn: items => {
			return items
		},
	})

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin
	const userId = user._id

	const { entities: projects, error, ids: projectIds, isLoading, isInitialized } = useSelector(state => state.projects)

	const newProjects = projectIds.map(id => projects[id]).filter(Boolean)

	var records = []

	if (newProjects.length > 0) {
		records = newProjects.map(item => {
			// projectAsset arrives populated from the list endpoints, but is a bare
			// id string on a freshly created project and null when its Location has
			// been hard-deleted. Read through locals so neither breaks the render.
			const asset = item.projectAsset && typeof item.projectAsset === 'object' ? item.projectAsset : {}
			const owner = item.owner || {}

			return {
				_id: item._id,
				projectName: item.projectName,
				assetName: asset.assetName,
				projectType: item.projectType,
				address: asset.address,
				city: asset.city,
				stateProvince: asset.stateProvince,
				postalCode: asset.postalCode,
				latitude: asset.latitude,
				longitude: asset.longitude,
				// isActive: item.isActive,
				image: asset.image,
				owner: owner._id,
				ownerName: owner.name,
				ownerImage: owner.image,
				ownerEmail: owner.email,
				ownerCompany: owner.company,
				updatedAt: item.updatedAt,
			}
		})
	}

	useEffect(() => {
		dispatch(initializeMyProjects())
	}, [dispatch, isInitialized])

	// The create response only populates `owner`, so refetch to get the populated
	// projectAsset the list rows (and the Recent sidebar links) are built from.
	const handleProjectCreated = () => {
		setCreateOpen(false)
		dispatch(initializeMyProjects())
	}

	const recordCount = records.length
	const { recordsAfterPagingAndSorting } = useTable(records, headerCells, filterFn, true)

	const handleSearchAll = e => {
		const { value } = e.target
		setSearchInput(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				const lowerValue = value.toLowerCase()
				return items.filter(
					x =>
						(x.projectName && x.projectName.toLowerCase().includes(lowerValue)) ||
						(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
						(x.address && x.address.toLowerCase().includes(lowerValue)) ||
						(x.city && x.city.toLowerCase().includes(lowerValue)) ||
						(x.stateProvince && x.stateProvince.toLowerCase().includes(lowerValue)) ||
						(x.postalCode && x.postalCode.toLowerCase().includes(lowerValue)) ||
						(x.projectType && x.projectType.toLowerCase().includes(lowerValue))
				)
			},
		})
		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	// Sort cards
	const sortedCardProjects = useMemo(() => {
		const projs = recordsAfterPagingAndSorting()
		if (!cardSortBy) return projs
		return [...projs].sort((a, b) => {
			let aVal = a[cardSortBy] ?? ''
			let bVal = b[cardSortBy] ?? ''
			if (cardSortBy === 'updatedAt') {
				aVal = new Date(aVal).getTime() || 0
				bVal = new Date(bVal).getTime() || 0
			} else {
				aVal = aVal.toString().toLowerCase()
				bVal = bVal.toString().toLowerCase()
			}
			if (aVal < bVal) return cardSortOrder === 'asc' ? -1 : 1
			if (aVal > bVal) return cardSortOrder === 'asc' ? 1 : -1
			return 0
		})
	}, [recordsAfterPagingAndSorting, cardSortBy, cardSortOrder])

	const CardSortIcon = ({ fieldId }) => {
		if (cardSortBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return cardSortOrder === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	const CardSortPills = () => (
		<div className="flex items-center gap-1.5 flex-wrap">
			<span className="text-xs text-gray-400 mr-0.5">Sort:</span>
			{cardSortFields.map(field => (
				<button
					key={field.id}
					type="button"
					onClick={() => handleCardSort(field.id)}
					className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
						cardSortBy === field.id ? 'bg-[#0080fe]/10 text-[#0080fe]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
					}`}
				>
					{field.label}
					<CardSortIcon fieldId={field.id} />
				</button>
			))}
		</div>
	)

	// No map on this collection, so on mobile there is exactly one legal view and
	// the toggle is not rendered at all (see customButtons below) — a one-option
	// pill group would be noise.
	//
	// Adopting the shared component also fixes a bug the hand-rolled copy had
	// here: BOTH pills called onToggleCardDisplay() unconditionally, so clicking
	// the already-active pill flipped the view. onChange sets an absolute value.
	const viewOptions = [
		{ value: 'cards', label: 'Cards', icon: LayoutGrid },
		{ value: 'list', label: 'List', icon: List },
	]

	// The boolean prop contract and its localStorage format are unchanged; the
	// string union is bridged here and never escapes this component.
	const renderViewToggle = () => (
		<ViewToggle
			value={cardDisplay ? 'cards' : 'list'}
			onChange={value => {
				if ((value === 'cards') !== cardDisplay) onToggleCardDisplay()
			}}
			options={viewOptions}
		/>
	)

	return (
		<div>
			<React.Fragment>
				{isLoading ? (
					<Spinner containerHeight="50vh" />
				) : !records ? (
					<Message variant="danger">{error}</Message>
				) : error ? (
					<Message variant="danger">{error}</Message>
				) : records.length === 0 ? (
					<ProjectsEmptyState onCreateProject={canCreateAssets ? () => setCreateOpen(true) : undefined} />
				) : (
					<>
						<Layout.PageToolbarFilter
							buttonText={canCreateAssets ? 'Create New Project' : undefined}
							buttonOnClick={canCreateAssets ? () => setCreateOpen(true) : undefined}
							customButtons={!listOnly ? (
								<div className="flex items-center gap-3">
									{cardDisplay && <CardSortPills />}
									{renderViewToggle()}
								</div>
							) : undefined}
							searchInputLabel=""
							searchInputRef={searchInputRef}
							searchInputValue={searchInput}
							searchInputOnChange={handleSearchAll}
						/>
						{cardDisplay ? (
							<ProjectsCards projects={sortedCardProjects} recordCount={recordCount} userId={userId} />
						) : (
							<ProjectsListCN
								projects={recordsAfterPagingAndSorting()}
								recordCount={recordCount}
								userId={userId}
							/>
						)}{' '}
					</>
				)}
			</React.Fragment>

			{canCreateAssets && (
				<CreateProjectModal
					open={createOpen}
					onClose={() => setCreateOpen(false)}
					onCreated={handleProjectCreated}
				/>
			)}
		</div>
	)
}

export default Projects
